import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControl, MenuItem, Typography } from '@mui/material';

import AlarmActiveSelect from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/alarm-active-select';
import ScenarioTitleRow from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/scenario-title-row';
import TimeFrames from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/time-frames/time-frames';
import {
  ActiveOption,
  useVideoScenarioConfiguration,
} from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/video-scenario-configuration/hooks';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './video-scenario-configuration.module.scss';

type Props = {
  scenarioTitle: string;
  index: number;
};

const VideoScenarioConfiguration = ({ scenarioTitle, index }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const { configuration, timeFrameValuesChanged, onAlarmActiveChanged, determineSelectedActiveOption, onPrivacyModusChanged } =
    useVideoScenarioConfiguration({
      index,
    });

  const {
    detailEditState: { viewingMode },
  } = use(ResidentSettingsProviderContext);

  const activeOptions = [
    <MenuItem data-testid={`when-active-alarm`} key={`when-active-alarm`} value={ActiveOption.AlarmsOnly}>
      {t('scenarioConfiguration.whenActiveAlarm')}
    </MenuItem>,
    <MenuItem data-testid={`every-situation`} key={`every-situation`} value={ActiveOption.Always}>
      {t('scenarioConfiguration.everySituation')}
    </MenuItem>,
  ];

  return (
    <div className={styles.container}>
      <ScenarioTitleRow scenarioTitle={scenarioTitle} description={t('scenarioConfiguration.scenarioDescription')} />

      <div className={styles.alarmActiveRow}>
        <AlarmActiveSelect
          label={t('scenarioConfiguration.alarmActiveLabel')}
          value={determineSelectedActiveOption()}
          options={activeOptions}
          onChange={onAlarmActiveChanged}
          disabled={viewingMode === 'viewing'}
        />
      </div>

      <Typography variant="subtitle1">{t('scenarioConfiguration.privacyModusDescription')}</Typography>

      <FormControl className={styles.privacyModusEnabledRow}>
        <Checkbox
          checked={configuration.isPrivacyModusEnabled}
          onChange={(_, enabled) => onPrivacyModusChanged(enabled)}
          disabled={viewingMode === 'viewing'}
        />
        <Typography variant="subtitle1">{t('scenarioConfiguration.privacyModusActiveLabel')}</Typography>
      </FormControl>

      <TimeFrames
        activeAllDay={configuration.activeAllDay}
        index={index}
        timeFrames={configuration.timeFrames}
        valuesChanged={timeFrameValuesChanged}
        timeFramesDescription={t('scenarioConfiguration.timeFramesDescriptionScenario')}
      />
    </div>
  );
};

export default VideoScenarioConfiguration;
